import React, { useState, useEffect } from "react";
import { Select, Spin, DatePicker } from "antd";
import moment from 'moment';
import { GetAccountTransactionDetailApi, GetAllTraderApi, getScalpingRecords, getSingleUserById, GetTraderAccountsByIdApi } from "../../api/request/client";
import columns from "../../columns/scalpingReportColumns";
import accountTransactionDetailColumns from "../../columns/ScalpingAccountDetails.js";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import UserAvatar from "../../components/global/avtar/UserAvatar.jsx";
import MyPagination from "../../components/global/pagination/Pagination.jsx";
import { useParams } from "react-router-dom";
import LoadableButton from "../../components/buttons/LoadableButton.jsx";
const { RangePicker } = DatePicker;


function ScalpingReport() {
  const { userId } = useParams();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [scalpingRecords, setScalpingRecords] = useState([]);
  const [totalStats, setTotalStats] = useState({})
  const [scalpingLoading, setScalpingLoading] = useState(false);
  const [accountTransactionDetailLoading, setAccountTransactionDetailLoading] = useState(false);
  const [dates, setDates] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [accountTransactionDetailPage, setAccountTransactionDetailPage] = useState(1);
  const [accountTransactionDetailPageSize, setAccountTransactionDetailPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [accountTransactionDetail, setAccountTransactionDetail] = useState([]);
  const [accountTransactionDetailTotalRecords, setAccountTransactionDetailTotalRecords] = useState(0);

  const getClients = async (query) => {
    try {
      setClientsLoading(true);
      let params = {
        ApplyPagination: false,
        SearchText: query || '',
      };
      const { data } = await GetAllTraderApi(new URLSearchParams(params).toString());
      if (data?.success) {
        const formattedClients = data.data.map(client => ({
          value: client.userId,
          label: (
            <div className="flex flex-col">
              <span className="font-medium">{client.name}</span>
              <span className="text-gray-500 text-sm">{client.email}</span>
            </div>
          ),
          title: client.name,
          email: client.email,
          name: client.name
        }));
        setClients(formattedClients);
      }
      setClientsLoading(false);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClientsLoading(false);
    }
  };

  const getAccounts = async (traderId) => {
    try {
      setAccountsLoading(true);
      setAccounts([]);
      const { data } = await GetTraderAccountsByIdApi(traderId, true);
      if (data?.success) {
        const formatted = data.data.map(acc => ({
          value: acc.login,
          label: (
            <div className="flex flex-col">
              <span className="font-medium">{acc.login}</span>
              <span className="text-gray-500 text-sm">Balance: {acc.balance ?? 0}</span>
            </div>
          ),
          title: acc.login,
          login: acc.login,
          balance: acc.balance,
          raw: acc,
        }));
        setAccounts(formatted);
      }
      setAccountsLoading(false);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccountsLoading(false);
    }
  };

  function customDebounce(func, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  const debouncedClientSearch = customDebounce((value) => {
    if (value.length >= 3) {
      getClients(value);
    }
  }, 500);

  useEffect(() => {
    // if userId is provided as a route param, fetch that user and preselect
    const init = async () => {
      if (userId) {
        try {
          setClientsLoading(true);
          const { data } = await getSingleUserById(userId);
          if (data?.success && data.data) {
            const u = data.data;
            const title = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim();
            const option = {
              value: u.userId,
              label: (
                <div className="flex flex-col">
                  <span className="font-medium">{title}</span>
                  <span className="text-gray-500 text-sm">{u.email}</span>
                </div>
              ),
              title,
              email: u.email,
              name: title,
            };
            setClients(prev => (prev.find(p => p.value === option.value) ? prev : [option, ...prev]));
            setSelectedClient(option);
            await getAccounts(u.userId);
          } else {
            // getClients();
          }
        } catch (err) {
          console.error('Error fetching user by id', err);
          getClients();
        } finally {
          setClientsLoading(false);
        }
      } else {
        // getClients();
      }
    };
    init();
  }, [userId]);

  const clientFilterOption = (input, option) => {
    const clientName = option?.name?.toString()?.toLowerCase() || "";
    const clientEmail = option?.email?.toString()?.toLowerCase() || "";
    const searchTerm = input.toLowerCase();
    return clientName.includes(searchTerm) || clientEmail.includes(searchTerm);
  };

  const accountFilterOption = (input, option) => {
    const login = option?.login?.toString() || "";
    const balance = option?.balance?.toString() || "";
    const searchTerm = input.toLowerCase();
    return login.includes(searchTerm) || balance.includes(searchTerm);
  };

  const handleClientChange = (value, option) => {
    setSelectedClient(option || null);
    setSelectedAccount(null);
    if (option && option.value) {
      getAccounts(option.value);
    } else {
      setAccounts([]);
    }
  };

  const handleAccountChange = (value, option) => {
    setSelectedAccount(option || null);
    setPage(1);
    // Do not auto-fetch here. Require user to click "Search" to fetch for the selected account.
    setHasSubmitted(false);
    setScalpingRecords([]);
    setTotalStats({
      "totalProfit": 0,
      "totalLoss": 0,
      "netProfit": 0,
    });
    setTotalRecords(0);
  };

  const fetchScalpingRecords = async (traderId) => {
    try {
      setScalpingLoading(true);
      // build query params for pagination and date range
      const params = new URLSearchParams({
        PageNumber: page,
        PageSize: pageSize,
        FromDate: dates.length === 2 ? dates[0].format('MM-DD-YYYY') : '',
        ToDate: dates.length === 2 ? dates[1].format('MM-DD-YYYY') : ''
      }).toString();

      const urlParam = params ? `${traderId}?${params}` : traderId;
      const { data } = await getScalpingRecords(urlParam);
      if (data?.success) {
        const records = data?.data?.data || [];
        setScalpingRecords(records.map((item, idx) => ({
          key: idx,
          ...item,
          userdetail: (<UserAvatar data={{ name: item?.fullName, email: item?.email, profileImage: item?.profileImage }} />),
          openTime: item.openTime ? moment(item.openTime).format('DD-MM-YYYY, hh:mm A') : '--',
          closeTime: item.closeTime ? moment(item.closeTime).format('DD-MM-YYYY, hh:mm A') : '--',
        })));
        setTotalStats({
          "totalProfit": data?.data?.totalProfit || 0,
          "totalLoss": data?.data?.totalLoss || 0,
          "netProfit": data?.data?.netProfit || 0,
        });
        setTotalRecords(data?.totalRecords || records.length);
      } else {
        setScalpingRecords([]);
        setTotalStats({
          "totalProfit": 0,
          "totalLoss": 0,
          "netProfit": 0,
        });
      }
      setScalpingLoading(false);
    } catch (error) {
      console.error('Error fetching scalping records', error);
      setScalpingLoading(false);
    }
  };

  // refetch when page or pageSize change OR when a manual search has been submitted
  useEffect(() => {
    if (hasSubmitted && selectedAccount && selectedAccount.login) {
      fetchScalpingRecords(selectedAccount.login);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted, page, pageSize]);

  const fetchAccountTransactionDetail = async (login) => {
    try {
      setAccountTransactionDetailLoading(true);
      const { data } = await GetAccountTransactionDetailApi(login);
      if (data?.success) {
        const records = data?.data || [];
        setAccountTransactionDetail(records.map((item, idx) => ({
          key: idx,
          ...item,
          openTime: item.openTime ? moment(item.openTime).format('DD-MM-YYYY, hh:mm A') : '--',
          closeTime: item.closeTime ? moment(item.closeTime).format('DD-MM-YYYY, hh:mm A') : '--',
        })));
        setAccountTransactionDetailTotalRecords(data?.totalRecords || records.length);
      } else {
        setAccountTransactionDetail([]);
      }
      setAccountTransactionDetailLoading(false);
    } catch (error) {
      console.error('Error fetching account transaction detail', error);
      setAccountTransactionDetailLoading(false);
    }
  };

  return (
    <>
      <div className="w-full flex gap-5 justify-between items-center ">
        <div className="flex gap-5 items-center">
          <div className="ant-select-selector-white mb-4 w-64">

            <Select
              showSearch
              placeholder="Search Client"
              optionFilterProp="children"
              value={selectedClient?.value}
              onSearch={(value) => {
                if (value.length < 3) {
                  setClients([]);
                  return;
                }
                debouncedClientSearch(value);
              }}
              onChange={handleClientChange}
              filterOption={clientFilterOption}
              loading={clientsLoading}
              options={clients}
              style={{ width: '100%' }}
              size="large"
              optionLabelProp="title"
              listHeight={300}
              suffixIcon={clientsLoading ? <Spin size="small" /> : undefined}
            />
          </div>

          <div className="ant-select-selector-white mb-4 w-64">
            {/* <label className="text-base text-[#2F2B3DCC] font-medium mb-2 block">
          Select Account
        </label> */}
            <Select
              showSearch
              placeholder="Select a account"
              optionFilterProp="children"
              value={selectedAccount?.value}
              onChange={handleAccountChange}
              filterOption={accountFilterOption}
              loading={accountsLoading}
              options={accounts}
              style={{ width: '100%' }}
              size="large"
              optionLabelProp="title"
              listHeight={300}
              disabled={!selectedClient}
              notFoundContent={
                accountsLoading ? (
                  <Spin size="small" />
                ) : (
                  // when not loading and there are no accounts for selected client
                  selectedClient && accounts.length === 0 ? (
                    <div className="text-gray-500">No record found</div>
                  ) : null
                )
              }
              suffixIcon={accountsLoading ? <Spin size="small" /> : undefined}
            />
          </div>

          <div className="mb-6 w-64 ">
            <RangePicker
              value={dates}
              className="w-full p-2"
              format="DD-MM-YYYY"
              onChange={(vals) => { setDates(vals || []); setPage(1); setHasSubmitted(false); }}

            />
          </div>
          <div className="mb-6 flex items-end">
            <LoadableButton
              lable="Submit"
              isLoading={scalpingLoading}
              loadingLable="Submitting..."
              className="bg-primary text-sm text-white font-semibold px-10 py-2 rounded-lg themeHover  duration-500"
              onClick={() => { if (!selectedAccount?.login) return; setPage(1); setHasSubmitted(true); fetchAccountTransactionDetail(selectedAccount.login); }}
              disabled={!selectedAccount?.login || scalpingLoading}
            />
          </div>
          {
            scalpingRecords?.length > 0 &&
            <div className="flex items-center gap-3 mb-4">
              <div className="text-lg text-[#2F2B3DCC] font-medium mb-2 block">
                Total Profit: <span className={`font-bold ${totalStats?.totalProfit > 0 ? 'text-green-500' : 'text-red-500'}`}>{totalStats?.totalProfit}</span>
              </div>
              <div className="text-lg text-[#2F2B3DCC] font-medium mb-2 block">
                Total Loss: <span className={`font-bold ${totalStats?.totalLoss > 0 ? 'text-red-500' : 'text-green-500'}`}>{totalStats?.totalLoss}</span>
              </div>
              <div className="text-lg text-[#2F2B3DCC] font-medium mb-2 block">
                Net Profit: <span className={`font-bold ${totalStats?.netProfit > 0 ? 'text-green-500' : 'text-red-500'}`}>{totalStats?.netProfit}</span>
              </div>
            </div>
          }
        </div>

      </div>
      <div className="w-full mt-3">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={scalpingRecords}
            loading={scalpingLoading}
            page={page}
            pageSize={pageSize}
            index={true}
          />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <MyPagination total={totalRecords} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} />
      </div>

      {accountTransactionDetail?.length > 0 && <div className="w-full mt-3">
        <div className="border-b border-gray-200 pb-2 mb-3"></div>
        <h3 className="text-xl text-[#2F2B3DCC] font-medium mb-3 block ps-3">Account Transaction Detail</h3>
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={accountTransactionDetailColumns}
            data={accountTransactionDetail}
            loading={scalpingLoading}
            page={page}
            pageSize={pageSize}
            index={true}
          />
        </div>
        <div className="flex justify-end mt-4">
          <MyPagination total={accountTransactionDetailTotalRecords} pageSize={accountTransactionDetailPageSize} setPageSize={setAccountTransactionDetailPageSize} page={accountTransactionDetailPage} setPage={setAccountTransactionDetailPage} />
        </div>
      </div>}
    </>
  );
}

export default ScalpingReport